import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FaArrowLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const Container = styled.div`
  padding: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  svg {
    font-size: 20px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
`;

const SearchContainer = styled.div`
  margin-bottom: 2rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FAQItem = styled.div`
  background-color: var(--bg-secondary);
  border-radius: 10px;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const QuestionButton = styled.button<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1.25rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  svg {
    font-size: 18px;
    transition: transform 0.2s;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'none'};
  }
`;

const Answer = styled.div<{ isOpen: boolean }>`
  padding: 0 1.25rem;
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqItems = [
    {
      question: t('help.faq.questions.q1'),
      answer: t('help.faq.answers.a1')
    },
    {
      question: t('help.faq.questions.q2'),
      answer: t('help.faq.answers.a2')
    },
    {
      question: t('help.faq.questions.q3'),
      answer: t('help.faq.answers.a3')
    },
    {
      question: t('help.faq.questions.q4'),
      answer: t('help.faq.answers.a4')
    },
    {
      question: t('help.faq.questions.q5'),
      answer: t('help.faq.answers.a5')
    }
  ];

  const filteredItems = faqItems.filter(item =>
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Container>
      <BackButton onClick={() => navigate('/profile/help')}>
        <FaArrowLeft />
        {t('common.back')}
      </BackButton>

      <Title>{t('help.faq.title')}</Title>

      <SearchContainer>
        <SearchInput
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('help.faq.searchPlaceholder')}
        />
      </SearchContainer>

      {filteredItems.map((item, index) => (
        <FAQItem key={index}>
          <QuestionButton
            isOpen={openIndex === index}
            onClick={() => toggleFAQ(index)}
          >
            {item.question}
            {openIndex === index ? <FaChevronUp /> : <FaChevronDown />}
          </QuestionButton>
          <Answer isOpen={openIndex === index}>
            {item.answer}
          </Answer>
        </FAQItem>
      ))}
    </Container>
  );
};

export default FAQ; 